import { AxiosInstance, AxiosResponse } from "axios";
import {
  InfinitePaginationRequestI,
  InfinitePaginationResponseI,
  SortRequestI,
} from "shared/lib/interfaces/api";
import { ListMemberUpdateMessageI } from "@/interfaces/list-dialing";
import {
  ListPillI,
  ListSubsectionI,
  PipelineListContactI,
} from "shared/lib/interfaces/lists";
import {
  ListDataTypesI,
  ListDataValueI,
  ListMetadataI,
  ListViewColumnFilterI,
  ListViewColumnFilterOperatorsI,
  ListViewColumnI,
  ListViewColumnSortI,
  ListViewColumnUnassignedI,
  ListViewRowI,
  LoadLeadsTimezoneI,
} from "@/interfaces/list";
import {
  GlobalSearchResultAccountI,
  GlobalSearchResultContactI,
} from "@/interfaces/global-search";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { LIST_KEYS } from "@/modules/list-workspace/constants";

export interface CreateOrUpdateListRequestParamsI {
  name: string;
  description?: string;
}

export interface GetListResponseI extends InfinitePaginationResponseI {
  contacts: PipelineListContactI[];
}

export interface CreateListResponseI {
  list_id: string;
}

export interface BulkUpdateListsRequestI {
  lists: {
    list_id: string;
    name: string;
    description: string;
    order: number;
  }[];
}

export interface GetListsRequestFilterParamsI {
  timezone_filters?: string[];
  campaign_filters?: string[];
  list_subsection?: ListSubsectionI;
  filter_available_to_call?: boolean;
}
export type ListKeysI = ValueOfObjectFields<typeof LIST_KEYS>;

export type GetListsRequestSortParamsI = SortRequestI<ListKeysI>;

export type GetListsRequestParamsI = InfinitePaginationRequestI &
  SortRequestI<ListKeysI> &
  GetListsRequestFilterParamsI;

export interface GetListsResponseI {
  user_lists: ListPillI[];
}

export interface GetListMemberResponseI {
  contact: PipelineListContactI;
}

export interface ContactToListI {
  contact_id: string;
  campaign_id: string;
}

export interface BulkAddContactsToListRequestI {
  contacts: ContactToListI[];
  list_ids: string[];
}

export interface BulkActionBaseRequestI {
  membership_ids: string[];
}

export interface ListCampaignI {
  id: string;
  name: string;
  logo_url: string;
}

export interface GetAvailableColumnsResponseI {
  column_types: ListViewColumnUnassignedI[];
}

export interface GetListColumnSelectOptionsResponseI {
  options: {
    id: string;
    value: string;
  }[];
}

export type GetListDetailsResponseI = ListMetadataI;

export interface GetListViewsResponseI {
  views: {
    view_id: string;
    display_name: string;
  }[];
}

export interface GetListViewMetadataResponseI extends ListMetadataI {
  view_id: string;
  filters: ListViewColumnFilterI[];
  sorts: ListViewColumnSortI[];
  columns: ListViewColumnI[];
}

export interface GetListViewRowsRequestParamsI {
  list_subsection: ListSubsectionI;
  search_term?: string;
  next_token?: string;
  filter_available_to_call?: boolean;
}

export interface GetListViewRowsResponseI {
  view_id: string;
  rows: ListViewRowI[];
  next_token?: string;
}

export interface GetListViewRowsByAccountIdResponseI {
  row: ListViewRowI;
}

export type BulkRemoveContactsFromListRequestI = BulkActionBaseRequestI;

export interface BulkDisqualifyContactsFromListRequestI extends BulkActionBaseRequestI {
  reason: string;
}

export type SearchContactsRequestI = InfinitePaginationRequestI & {
  search_term: string;
};

export interface LoadNewAssignedLeadsRequestI {
  filter_timezones?: LoadLeadsTimezoneI[] | null;
}

export interface AvailableToCallColdLeadsForUserI {
  list_id?: string;
  list_name?: string;
  count?: number;
}

export interface LoadNewAssignedLeadsResponseI {
  available_to_call_cold_leads_for_user: AvailableToCallColdLeadsForUserI[];
}

export type GetConnectedListMemberResponseI = ListMemberUpdateMessageI;

export interface AddColumnRequestI {
  name: string;
  // this is optional. If provided, will disregard other info provided below.
  // providing a column_typ_id means you're adding a column that already exists to your view.
  // omitting the column_type_id means you're adding a new column (that doesn't exist yet) to your view.
  column_type_id?: string;
  data_type?: ListDataTypesI;
}

export type AddColumnResponseI = ListViewColumnI;

export interface AddFilterRequestI {
  column_type_id: string;
  value: ListDataValueI;
  filter_operator: ListViewColumnFilterOperatorsI;
}

export type AddFilterResponseI = ListViewColumnFilterI;

export type UpdateFilterRequestI = AddFilterRequestI;

export type UpdateFilterResponseI = ListViewColumnFilterI;

export interface UpdateCellRequestI {
  routeParams: {
    viewId: string;
    cellId: string;
  };
  queryParams: {
    //string is for text, number, date, etc.
    //string[] is for select/multi-select data-types
    value?: string | string[] | number | boolean;
  };
}

export interface UpdateCellResponseI {
  row_id: string;
  column_type_id: string;
}

export interface AddSortRequestParamsI {
  column_type_id: string;
  is_ascending: boolean;
}

export type UpdateSortRequestParamsI = AddSortRequestParamsI;

export type UpdateSortResponseI = ListViewColumnSortI;

export type AddSortResponseI = ListViewColumnSortI;

export interface UpdateColumnRequestParamsI {
  order?: number;
  is_hidden?: boolean;
  is_frozen?: boolean;
  name?: string;
}

export type UpdateColumnResponseI = ListViewColumnI;

export interface SearchGlobalRequestI {
  search_term: string;
}

export interface SearchGlobalResponseI {
  contacts: GlobalSearchResultContactI[];
  accounts: GlobalSearchResultAccountI[];
}
export interface ListApiI {
  getLists: () => Promise<AxiosResponse<GetListsResponseI>>;
  createList: (
    listParameters: CreateOrUpdateListRequestParamsI
  ) => Promise<AxiosResponse<CreateListResponseI>>;

  getListDetails: (
    listId: string
  ) => Promise<AxiosResponse<GetListDetailsResponseI>>;

  getListMember: (
    listId: string,
    membershipId: string
  ) => Promise<AxiosResponse<GetListMemberResponseI>>;

  updateList: (
    listId: string,
    listParameters: CreateOrUpdateListRequestParamsI
  ) => Promise<AxiosResponse>;

  deleteList: (listId: string) => Promise<AxiosResponse>;

  // The new list contacts endpoint that supports nurturing / cold lists.
  getListContacts: (
    listId: string,
    parameters?: GetListsRequestParamsI
  ) => Promise<AxiosResponse<GetListResponseI>>;

  //Used to update leads for a specific account in the current list
  //which is also includes currently dialing list
  getListContactsByAccountId: (
    campaignId: string,
    listId: string,
    accountId: string,
    queryParams: {
      list_subsection: ListSubsectionI;
    }
  ) => Promise<AxiosResponse<GetListResponseI>>;

  bulkUpdateLists: (lists: BulkUpdateListsRequestI) => Promise<AxiosResponse>;

  bulkAddContactsToListRequest: (
    params: BulkAddContactsToListRequestI
  ) => Promise<AxiosResponse>;

  bulkRemoveContactsFromListRequest: (
    listId: string,
    params: BulkRemoveContactsFromListRequestI
  ) => Promise<AxiosResponse>;

  bulkDisqualifyContactsFromListRequest: (
    listId: string,
    params: BulkDisqualifyContactsFromListRequestI
  ) => Promise<AxiosResponse>;

  searchContacts: (
    parameters: SearchContactsRequestI
  ) => Promise<AxiosResponse<GetListResponseI>>;

  searchGlobal: (
    parameters: SearchGlobalRequestI
  ) => Promise<AxiosResponse<SearchGlobalResponseI>>;

  getConnectedListMember: (
    listId: string,
    callId: string
  ) => Promise<AxiosResponse<GetConnectedListMemberResponseI>>;

  loadNewAssignedLeads: (
    listId: string,
    params: LoadNewAssignedLeadsRequestI
  ) => Promise<AxiosResponse<LoadNewAssignedLeadsResponseI>>;

  getListViews: (
    listId: string
  ) => Promise<AxiosResponse<GetListViewsResponseI>>;

  getListViewMetadata: (
    viewId: string
  ) => Promise<AxiosResponse<GetListViewMetadataResponseI>>;

  getListViewRows: (
    viewId: string,
    params: GetListViewRowsRequestParamsI
  ) => Promise<AxiosResponse<GetListViewRowsResponseI>>;

  getListViewRowsByAccountId: (
    viewId: string,
    campaignId: string,
    accountId: string
  ) => Promise<AxiosResponse<GetListViewRowsByAccountIdResponseI>>;

  getAvailableColumns: () => Promise<
    AxiosResponse<GetAvailableColumnsResponseI>
  >;

  getListColumnSelectOptions: (
    columnTypeId: string
  ) => Promise<AxiosResponse<GetListColumnSelectOptionsResponseI>>;

  addColumn: (
    viewId: string,
    params: AddColumnRequestI
  ) => Promise<AxiosResponse<AddColumnResponseI>>;

  updateColumn: (
    viewId: string,
    columnId: string,
    params: UpdateColumnRequestParamsI
  ) => Promise<AxiosResponse<UpdateColumnResponseI>>;

  addFilter: (
    viewId: string,
    params: AddFilterRequestI
  ) => Promise<AxiosResponse<AddFilterResponseI>>;

  updateFilter: (
    viewId: string,
    filterId: string,
    params: UpdateFilterRequestI
  ) => Promise<AxiosResponse<UpdateFilterResponseI>>;

  deleteFilter: (viewId: string, filterId: string) => Promise<AxiosResponse>;

  updateCell: ({
    routeParams: { viewId, cellId },
    queryParams: { value },
  }: UpdateCellRequestI) => Promise<AxiosResponse<UpdateCellResponseI>>;

  addSort: (
    viewId: string,
    params: AddSortRequestParamsI
  ) => Promise<AxiosResponse<AddSortResponseI>>;

  updateSort: (
    viewId: string,
    sortId: string,
    params: UpdateSortRequestParamsI
  ) => Promise<AxiosResponse<UpdateSortResponseI>>;

  deleteSort: (viewId: string, sortId: string) => Promise<AxiosResponse>;

  downloadListCSV: (listId: string) => Promise<AxiosResponse<Blob>>;
}

export const ListAPIEndpoints = (API: AxiosInstance): ListApiI => ({
  getListContacts: (listId: string, params) =>
    API.post(`/v2/list/${listId}`, params),

  getLists: () => API.get("/v1/lists"),

  createList: (listParameters: CreateOrUpdateListRequestParamsI) =>
    API.post("/v1/lists", listParameters),

  bulkUpdateLists: (params) => API.post(`/v1/bulk_list_updates`, params),

  getListDetails: (listId) => API.get(`/v1/lists/${listId}/details`),

  updateList: (listId, listParameters) =>
    API.patch(`/v1/lists/${listId}`, listParameters),

  deleteList: (listId) => API.delete(`/v1/lists/${listId}`),

  getListContactsByAccountId: (campaignId, listId, accountId, queryParams) =>
    API.get(
      `/v1/list/${listId}/account/${accountId}/campaign/${campaignId}/leads`,
      {
        params: queryParams,
      }
    ),

  bulkAddContactsToListRequest: (params) =>
    API.post(`/v1/list/bulk_add_contacts`, params),

  bulkRemoveContactsFromListRequest: (listId, params) =>
    API.post(`/v1/list/${listId}/bulk_remove_contacts`, params),

  bulkDisqualifyContactsFromListRequest: (listId, params) =>
    API.post(`/v1/list/${listId}/bulk_disqualify_leads`, params),

  searchContacts: (parameters) => API.post(`/v1/list_search`, parameters),

  searchGlobal: (params) => API.post(`/v2/list_search`, params),

  getListMember: (listId, membershipId) =>
    API.get(`/v1/list/${listId}/membership/${membershipId}/status`),

  getConnectedListMember: (listId, callId) =>
    API.get(`/v1/list/${listId}/call/${callId}/get_membership`),

  loadNewAssignedLeads: (listId, params) =>
    API.post(`/v1/list/${listId}/pull_leads_to_work`, params),

  getListViews: (listId) => API.get(`/v1/list/${listId}/available_views`),

  getListViewMetadata: (viewId) => API.get(`/v1/view/${viewId}/metadata`),

  getListViewRows: (viewId, params) =>
    API.post(`/v1/view/${viewId}/rows`, params),

  getListViewRowsByAccountId: (viewId, campaignId, accountId) =>
    API.get(
      `/v1/view/${viewId}/campaign_id/${campaignId}/account/${accountId}/row`
    ),

  getAvailableColumns: () => API.get("/v1/view/available_columns"),

  getListColumnSelectOptions: (columnTypeId) =>
    API.get(`/v1/view/column_type/${columnTypeId}/select_options`),

  addColumn: (viewId, params) => API.post(`/v1/view/${viewId}/column`, params),

  updateColumn: (viewId, columnId, params) =>
    API.patch(`/v1/view/${viewId}/column/${columnId}`, params),

  addFilter: (viewId, params) => API.post(`/v1/view/${viewId}/filter`, params),

  updateFilter: (viewId, filterId, params) =>
    API.patch(`/v1/view/${viewId}/filter/${filterId}`, params),

  deleteFilter: (viewId, filterId) =>
    API.delete(`/v1/view/${viewId}/filter/${filterId}`),

  updateCell: ({ routeParams: { viewId, cellId }, queryParams: { value } }) =>
    API.patch(`/v1/view/${viewId}/cell/${cellId}`, {
      value,
    }),

  addSort: (viewId, params) => API.post(`/v1/view/${viewId}/sort`, params),

  updateSort: (viewId, sortId, params) =>
    API.patch(`/v1/view/${viewId}/sort/${sortId}`, params),

  deleteSort: (viewId, sortId) =>
    API.delete(`/v1/view/${viewId}/sort/${sortId}`),

  downloadListCSV: (listId: string) =>
    API.get(`/v1/list/${listId}/export_csv`, {
      responseType: "blob",
    }),
});
